import React from "react";
import { Space, Table, Typography } from "antd";
import dayjs from "dayjs";
import { FIELD_TYPES } from "../../../domain/clinicalForms";
import { getFileIcon } from "../../../helpers/getFileIcon";
import FilePreview from "../../ui/FilePreview";
import PillTag from "./PillTag";
import { isEmpty, optionLabel } from "./utils";

export const EMPTY_MARK = (
  <Typography.Text type="secondary">—</Typography.Text>
);

const tableColumnWidth = (col) => {
  if (col.type === "textarea" || col.type === "multiselect") return 180;
  if (col.type === "checkbox" || col.type === "radio") return 180;
  if (col.type === "datetime") return 160;
  if (col.type === "date") return 130;
  if (col.type === "file") return 160;
  return 120;
};

const findColOptLabel = (col, val) =>
  (col.options || []).find((o) => o.value === val)?.label ?? val;

const renderTableCell = (col, v) => {
  const empty =
    v === "" || v == null || (Array.isArray(v) && v.length === 0);
  if (empty) return <Typography.Text type="secondary">—</Typography.Text>;

  if (col.type === "date") {
    return (
      <Typography.Text>{dayjs(v).format("DD/MM/YYYY")}</Typography.Text>
    );
  }
  if (col.type === "datetime") {
    return (
      <Typography.Text>
        {dayjs(v).format("DD/MM/YYYY hh:mm A")}
      </Typography.Text>
    );
  }
  if (col.type === "select" || col.type === "radio") {
    return <PillTag>{findColOptLabel(col, v)}</PillTag>;
  }
  if (col.type === "multiselect" || col.type === "checkbox") {
    const values = Array.isArray(v) ? v : [];
    return (
      <Space size={[4, 4]} wrap>
        {values.map((sv) => (
          <PillTag key={sv}>{findColOptLabel(col, sv)}</PillTag>
        ))}
      </Space>
    );
  }
  if (col.type === "file") {
    if (typeof v === "string") return <PillTag>{v}</PillTag>;
    if (v?.path) return <FilePreview file={v} />;
    const name = v?.file_name || v?.name || "Uploaded";
    return <PillTag>{name}</PillTag>;
  }
  return <Typography.Text>{String(v)}</Typography.Text>;
};

const renderTable = (field, value) => {
  const cols = field.columns || [];
  const rowsValue = Array.isArray(value) ? value : [];
  if (!cols.length || !rowsValue.length) return EMPTY_MARK;

  const data = rowsValue.map((row, idx) => {
    const record = { key: idx };
    cols.forEach((col) => {
      record[col.id] = row?.[col.id];
    });
    return record;
  });

  const columns = cols.map((col) => ({
    title: col.label,
    dataIndex: col.id,
    key: col.id,
    width: tableColumnWidth(col),
    render: (v) => renderTableCell(col, v),
  }));

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      dataSource={data}
      columns={columns}
      tableLayout="fixed"
      scroll={{ x: cols.reduce((s, c) => s + tableColumnWidth(c), 0) }}
    />
  );
};

const renderFile = (value) => {
  if (!value) return EMPTY_MARK;
  if (typeof value === "string") return <PillTag>{value}</PillTag>;
  if (!value.path) {
    const name =
      value.file_name ||
      value.name ||
      value.path?.split("/").pop() ||
      "Uploaded";
    return (
      <Space size={8}>
        {getFileIcon(value)}
        <Typography.Text>{name}</Typography.Text>
      </Space>
    );
  }
  return <FilePreview file={value} />;
};

const renderValue = (field, value) => {
  if (field.type !== FIELD_TYPES.TABLE && isEmpty(value)) return EMPTY_MARK;

  switch (field.type) {
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.TEXTAREA:
      return (
        <Typography.Text style={{ whiteSpace: "pre-wrap" }}>
          {String(value)}
        </Typography.Text>
      );
    case FIELD_TYPES.NUMBER:
      return <PillTag>{String(value)}</PillTag>;
    case FIELD_TYPES.DATE:
      return <PillTag>{dayjs(value).format("DD/MM/YYYY")}</PillTag>;
    case FIELD_TYPES.DATETIME:
      return <PillTag>{dayjs(value).format("DD/MM/YYYY hh:mm A")}</PillTag>;
    case FIELD_TYPES.SELECT:
    case FIELD_TYPES.RADIO:
      return <PillTag>{optionLabel(field.options, value)}</PillTag>;
    case FIELD_TYPES.MULTISELECT:
    case FIELD_TYPES.CHECKBOX:
      return (
        <Space size={[6, 6]} wrap>
          {(Array.isArray(value) ? value : []).map((v) => (
            <PillTag key={v}>{optionLabel(field.options, v)}</PillTag>
          ))}
        </Space>
      );
    case FIELD_TYPES.SLIDER:
      return <PillTag>{String(value)}</PillTag>;
    case FIELD_TYPES.TABLE:
      return renderTable(field, value);
    case FIELD_TYPES.FILE:
      return renderFile(value);
    default:
      return <Typography.Text>{String(value)}</Typography.Text>;
  }
};

export default renderValue;
